import type { ReactElement } from 'react';

const CASES = [
  {
    num: '01', tag: 'GAMES',
    title: 'Agents as NPCs, co-op partners, adversaries.',
    body: 'Give every character its own LLM brain. Claim actors 1:1, submit durable movement and dialogue intents, watch the world respond in real time.',
    bullets: ['claim_actor · move_actor · call_actor_function', 'per-NPC scene capture via capture_actor_view', 'M:N mapping — 1 agent, many actors'],
    accent: 'mag' as const,
  },
  {
    num: '02', tag: 'INDUSTRIAL SIM',
    title: 'Warehouses, factories, ports — under agent supervision.',
    body: 'Model your operation in UE, expose the actors, let agents run scenarios overnight. Every command is logged, every outcome auditable.',
    bullets: ['Scheduled T0 anchors for multi-actor choreography', 'Deferred intents · run_late / drop policies', 'Full command_log · zero silent failures'],
    accent: 'cyan' as const,
  },
  {
    num: '03', tag: 'DATA GATHERING',
    title: 'Multi-modal training data, generated at scale.',
    body: 'Vision + kinematics + labels, straight from the engine. Screenshot the viewport, capture per-NPC views, drive controlled variations.',
    bullets: ['get_viewport_screenshot · deterministic seeds', 'Timestamp-aligned event streams', 'Runtime actor discovery — no manual catalog'],
    accent: 'mag' as const,
  },
  {
    num: '04', tag: 'DIGITAL TWINS',
    title: 'Operate the physical from inside the virtual.',
    body: 'A twin of your port, plant or building — with an agent watching, planning, and dispatching. UE stays authoritative. The cloud stays durable. The agent stays out of the hot loop.',
    bullets: ['Prediction buffer keeps motion smooth', 'Agent thinks in seconds, UE ticks at 60fps'],
    accent: 'cyan' as const,
  },
  {
    num: '05', tag: 'AI FILM',
    title: 'Direct actors, cameras and lights with a prompt.',
    body: 'Machinima where the director is an agent. Move the camera, cue the montage, spawn the extras — from the same MCP surface.',
    bullets: ['actor_type: camera · light · fx_system', 'Scheduled anchors sync cross-actor timelines'],
    accent: 'mag' as const,
  },
];

export function UseCases(): ReactElement {
  return (
    <section className="landing-usecases" id="usecases">
      <div className="landing-section-head">
        <div className="landing-section-eyebrow">
          <span className="landing-brk">[</span> 04 · APPLICATIONS <span className="landing-brk">]</span>
        </div>
        <h2 className="landing-section-title">
          One platform. <span className="landing-glow-mag">Five worlds of use.</span>
        </h2>
      </div>

      <div className="landing-uc-grid">
        {CASES.map((c, i) => (
          <div key={i} className={`landing-uc-card landing-uc-${i + 1}`}>
            <div className="landing-uc-num">{c.num}</div>
            <div className="landing-uc-tag">{c.tag}</div>
            <h3 className="landing-uc-title">{c.title}</h3>
            <p className="landing-uc-body">{c.body}</p>
            <ul className="landing-uc-list landing-mono">
              {c.bullets.map((b, j) => (
                <li key={j}>
                  <span className={c.accent === 'mag' ? 'landing-text-mag' : 'landing-text-cyan'}>→</span>{' '}
                  {b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
